import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Clock,
  CheckCircle,
  XCircle,
  Loader,
  RefreshCw,
  AlertTriangle,
  Eye,
  Filter,
  Calendar
} from 'lucide-react'
import Card from '../ui/Card'
import Button from '../ui/Button'
import Pagination from '../ui/Pagination'
import ErrorBoundary from '../ui/ErrorBoundary'
import ImportDetailsModal from './ImportDetailsModal'
import { useTMSImports } from '../../hooks/useTMSImports'

const ImportHistory = () => {
  const { t } = useTranslation()
  // FORCE CACHE REFRESH - 2025-09-24-10:43
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedImport, setSelectedImport] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)

  // Debug selectedImport state changes
  useEffect(() => {
    console.log('🔍 selectedImport state changed:', {
      selectedImport,
      status: selectedImport?.status,
      import_id: selectedImport?.import_id,
      truthy: !!selectedImport
    });

    if (selectedImport) {
      console.log('✅ selectedImport is truthy, modal should render');
    } else {
      console.log('❌ selectedImport is falsy, modal will not render');
    }
  }, [selectedImport])

  const pageSize = 10

  const {
    imports,
    totalCount,
    totalPages,
    hasActiveImports,
    isLoadingHistory,
    historyError,
    refetchHistory
  } = useTMSImports(currentPage, pageSize, statusFilter)

  // Debug imports data (after hook is called)
  useEffect(() => {
    console.log('🔍 ImportHistory render:', {
      importsLength: imports.length,
      firstImportId: imports[0]?.import_id, // Log first import ID only
      isLoadingHistory,
      historyError
    });
  }, [imports, isLoadingHistory, historyError])

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />
      case 'processing':
        return <Loader className="w-4 h-4 text-blue-500 animate-spin" />
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
      case 'processing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  // Reset to page 1 when filter changes
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus)
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  // Separate, safe click handler function
  const handleImportCardClick = (importItem) => {
    try {
      console.log('🎯 handleImportCardClick called for:', importItem.import_id, 'status:', importItem.status);

      // Ultra-safe data sanitization
      const safeImport = {
        import_id: importItem.import_id,
        job_uuid: importItem.job_uuid,
        project_id: importItem.project_id,
        project_name: importItem.project_name,
        status: importItem.status,
        started_at: importItem.started_at,
        completed_at: importItem.completed_at,
        error_message: importItem.error_message,
        retry_count: importItem.retry_count,
        settings_applied: importItem.settings_applied,
        // Only include safe statistics properties
        statistics: importItem.statistics ? {
          upload_id: importItem.statistics.upload_id,
          status: importItem.statistics.status,
          processed_keys: importItem.statistics.processed_keys,
          created_keys: importItem.statistics.created_keys,
          updated_keys: importItem.statistics.updated_keys,
          created_translations: importItem.statistics.created_translations,
          updated_translations: importItem.statistics.updated_translations,
          processed_translations: importItem.statistics.processed_translations,
          error_count: importItem.statistics.error_count
        } : null
      };

      console.log('🔧 Setting selectedImport to safe data');
      setSelectedImport(safeImport);
      console.log('✅ Modal should open now');

    } catch (error) {
      console.error('❌ Error in handleImportCardClick:', error);
      alert('Error opening import details. Check console for details.');
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  const getProgressText = (importItem) => {
    try {
      if (importItem.status === 'completed' && importItem.statistics) {
        // Safely extract only primitive values from statistics
        const processedKeys = importItem.statistics?.processed_keys || 0
        if (typeof processedKeys === 'number') {
          return `${processedKeys} keys processed`
        }
        console.warn('⚠️ processed_keys is not a number:', typeof processedKeys, processedKeys)
        return 'Completed'
      }
      if (importItem.status === 'failed' && importItem.error_message) {
        return importItem.error_message
      }
      return t(`tms.import.status.${importItem.status}`)
    } catch (error) {
      console.error('❌ Error in getProgressText:', error, 'for import:', importItem.import_id)
      return importItem.status || 'Unknown'
    }
  }

  if (isLoadingHistory) {
    return (
      <Card>
        <Card.Content className="p-6">
          <div className="flex items-center justify-center space-x-3">
            <Loader className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-gray-600 dark:text-gray-400">
              {t('tms.loadingImportHistory')}
            </span>
          </div>
        </Card.Content>
      </Card>
    )
  }

  if (historyError) {
    return (
      <Card className="border-red-200 dark:border-red-800">
        <Card.Content className="p-6">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="text-lg font-medium text-red-900 dark:text-red-100">
                {t('tms.importHistoryError')}
              </h3>
              <p className="text-red-600 dark:text-red-400 mt-1">
                {historyError?.response?.data?.detail || historyError?.message || t('tms.genericError')}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchHistory()}
                className="mt-3 flex items-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('common.retry')}
              </Button>
            </div>
          </div>
        </Card.Content>
      </Card>
    )
  }

  return (
    <div className="space-y-6" data-testid="import-history-section">
      {/* Header with status indicator */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('tms.importHistory')} ({totalCount})
          </h3>
          {hasActiveImports && (
            <div className="flex items-center mt-1 text-sm text-blue-600 dark:text-blue-400">
              <Loader className="w-4 h-4 mr-1 animate-spin" />
              {t('tms.activeImportsRunning')}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Status Filter */}
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => handleStatusFilterChange(e.target.value)}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            <option value="all">{t('tms.allStatuses')}</option>
            <option value="pending">{t('tms.pending')}</option>
            <option value="processing">{t('tms.processing')}</option>
            <option value="completed">{t('tms.completed')}</option>
            <option value="failed">{t('tms.failed')}</option>
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetchHistory()}
            className="flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            {t('common.refresh')}
          </Button>
        </div>
      </div>

      {/* Import List */}
      {imports.length === 0 ? (
        <Card className="text-center py-12">
          <Card.Content>
            <div className="flex flex-col items-center space-y-4">
              <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600" />
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {statusFilter === 'all' ? t('tms.noImportsYet') : t('tms.noImportsForStatus')}
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('tms.importHistoryEmptyDescription')}
                </p>
              </div>
            </div>
          </Card.Content>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="import-history-table">
          {imports.map((importItem) => (
            <ErrorBoundary
              key={`error-boundary-${importItem.import_id}`}
              fallbackMessage="Import card unavailable"
              showError={process.env.NODE_ENV === 'development'}
            >
              <Card
                key={importItem.import_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                data-testid="import-card"
                data-import-id={importItem.import_id}
                data-import-status={importItem.status}
                data-job-id={importItem.job_uuid}
                onClick={(e) => {
                  console.log('🎯 Card clicked - any status!', importItem.status);
                  e.stopPropagation();
                  handleImportCardClick(importItem);
                }}
            >
              <Card.Content className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {getStatusIcon(importItem.status)}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {importItem.project_name || importItem.project_id}
                        </span>
                        <span
                          data-testid="status-badge"
                          className={`px-2 py-1 text-xs rounded-full ${getStatusColor(importItem.status)}`}
                        >
                          {t(`tms.status.${importItem.status}`)}
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                        <div>
                          <span className="font-medium">{t('tms.job')}:</span>
                          <span className="ml-1 font-mono text-xs">
                            {importItem.job_uuid.substring(0, 8)}...
                          </span>
                        </div>
                        <div>{getProgressText(importItem)}</div>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3 mr-1" />
                          {formatDate(importItem.started_at)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {importItem.retry_count > 0 && (
                      <span className="text-xs text-orange-500 bg-orange-100 dark:bg-orange-900 px-2 py-1 rounded">
                        {t('tms.retries', { count: importItem.retry_count })}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log('👁️ Eye button clicked');
                        handleImportCardClick(importItem);
                      }}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card.Content>
            </Card>
            </ErrorBoundary>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        </div>
      )}

      {/* Import Details Modal */}
      {selectedImport && (
        <ImportDetailsModal
          importData={selectedImport}
          onClose={() => {
            console.log('🔧 Modal onClose called, clearing selectedImport');
            setSelectedImport(null);
          }}
        />
      )}
      {selectedImport && console.log('🎯 Conditional render: Modal should be visible now')}
    </div>
  )
}

export default ImportHistory